import React, { useCallback, useEffect, useRef, useState } from "react";
import { usePagination } from "../../hooks/usePagination";
import Header from "./Header";
import SearchControls from "./SearchControls";
import TableSkeleton from "./TableSkeleton";
import NoDataFound from "./NoDataFound";
import Pagination from "./Pagination";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import { debounce } from 'lodash';
import { showErrorToast, showSuccessToast } from "../common";



function EntityListPage({
  title,
  subtitle,
  addButtonText,
  searchPlaceholder,
  searchChildren,

  fetchHook,
  deleteHook,

  searchFilter,
  filterFn,
  extraFilters,
  tableColumns,

  TableRow,
  Card,
  AddEditModal,

  emptyIcon,
  emptyText,
  // emptyDesc,
  buttonText,
  getNoDataDesc,

  deleteMsg
}) {
  const { data, refetch, isLoading } = fetchHook();
  const { mutate: deleteItem } = deleteHook();

  const [list, setList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [viewMode, setViewMode] = useState("table");

  const [isPopupOpen, setPopupOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isEditItem, setIsEditItem] = useState(null);

  const [deleteItemData, setDeleteItemData] = useState(null);
  const [isDeleteOpen, setDeleteOpen] = useState(false);

  const listRef = useRef([]);  

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const noDataDesc = getNoDataDesc?.(searchText);
  useEffect(() => {
    if (data) {
      setList(data);
      setFilteredList(data);
      listRef.current = data;
    }
  }, [data]);

  // const handleSearch = (value) => {
  //   setSearchText(value);
  //   if (!value) {
  //     setFilteredList(listRef.current);
  //     return;
  //   }
  //   setFilteredList(searchFilter(listRef.current, value));
  //   setCurrentPage(1);
  // };

  // const debouncedSearch = useCallback(debounce(handleSearch, 800), []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!Array.isArray(listRef.current)) return;

      const result = filterFn(
        listRef.current,
        searchText,
        extraFilters
      );

      setFilteredList(result);
      setCurrentPage(1);
    }, 600);

    return () => clearTimeout(timer);
  }, [searchText,extraFilters, filterFn]);

 
  const totalItems = filteredList.length;

  const {
    totalPages,
    startIndex,
    endIndex,
    pageNumbers,
    onPageChange,
    onItemsPerPageChange,
  } = usePagination({
    totalItems,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
  });

  const currentItems = filteredList.slice(startIndex, endIndex);

  const confirmDelete = () => {
    setDeleteOpen(false);
    deleteItem(deleteItemData, {
      onSuccess: async (res) => {
        refetch();
        showSuccessToast(res?.data?.message);
      },
      onError: (err) => {
        showErrorToast(err.response.data.error)
      },
    });
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto px-6 py-6">
          <Header
            title={title}
            subTitle={subtitle}
            buttonText={addButtonText}
            handleClick={() => setPopupOpen(true)}
          />

          <SearchControls
            searchText={searchText}
            onSearchChange={setSearchText}
            onSearchDebounced={setSearchText}
            placeholder={searchPlaceholder}
            viewMode={viewMode}
            onViewChange={setViewMode}
            totalItems={totalItems}
          >
            {searchChildren}
          </SearchControls>

          <div className="bg-white rounded-xl shadow-card overflow-hidden">
            {isLoading ? (
              <TableSkeleton columns={tableColumns.map(c => c.width)} />
            ) : (
              <>
                {viewMode === "table" ? (
                  <>
                    {/* Table Header */}
                    <div className="bg-gray-50 border-b">
                      <div className="flex px-6 py-4 text-sm font-semibold text-gray-700">
                        {tableColumns.map(col => (
                          <div key={col.key} className={col.className}>
                            {col.label}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Table Body */}
                    {currentItems && currentItems.length > 0 ? (
                      currentItems.map((item, index) => (
                        <TableRow
                          key={index}
                          item={item}
                          index={index}
                          startIndex={startIndex}
                          onEdit={(e) => {
                            e.stopPropagation();
                            setIsEdit(true);
                            setIsEditItem(item);
                            setPopupOpen(true);
                          }}
                          onDelete={(e) => {
                            e.stopPropagation();
                            setDeleteItemData(item);
                            setDeleteOpen(true);
                          }}
                        />
                      ))
                    ) : (
                      <NoDataFound
                        icon={emptyIcon}
                        text={emptyText}
                        desc={noDataDesc}
                        showButton={!searchText}
                        handleClick={() => setPopupOpen(true)}
                        buttonText={buttonText}
                      />
                    )}
                  </>
                ) : (
                  <div className="p-6">
                    {currentItems && currentItems.length > 0 ? (

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {currentItems.map((item, index) => (
                          <Card
                            key={index}
                            item={item}
                            onEdit={() => {
                              setIsEdit(true);
                              setIsEditItem(item);
                              setPopupOpen(true);
                            }}
                            onDelete={() => {
                              setDeleteItemData(item);
                              setDeleteOpen(true);
                            }}
                          />
                        ))}
                      </div>)

                      : (<NoDataFound
                        icon={emptyIcon}
                        text={emptyText}
                        desc={noDataDesc}
                        showButton={!searchText}
                        handleClick={() => setPopupOpen(true)}
                        buttonText={buttonText}
                      />)}

                  </div>
                )}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  pageNumbers={pageNumbers}
                  itemsPerPage={itemsPerPage}
                  totalItems={totalItems}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  onPageChange={onPageChange}
                  onItemsPerPageChange={onItemsPerPageChange}
                />
              </>
            )}
          </div>
        </div>
      </div>
      <AddEditModal
        isOpen={isPopupOpen}
        onClose={(data) => {
          setPopupOpen(false);
          setIsEdit(false);
          setIsEditItem(null);
          if(data?.isRefresh || data === true){
            refetch();
          }
        }}
        isEdit={isEdit}
        isEditItem={isEditItem}
      />

      {isDeleteOpen && (
        <ConfirmDeleteModal
          onCancel={() => setDeleteOpen(false)}
          onConfirm={confirmDelete}
          deleteMsg={deleteMsg}
        />
      )}

    </>
  );
}

export default EntityListPage;
